// Manual sections 9–13: the four ways recordings get into the system — manual upload,
// Genesys fetch, Cloud Connector, scheduled runs — and the Jobs page that tracks them.
// See ../index.js for the schema.

const ingestion = [
  {
    id: 'upload',
    group: 'Bring calls in',
    title: 'Upload recordings',
    lede: 'Audio you already have, dragged straight in.',
    roles: ['sAdm', 'adm', 'qa'],
    blocks: [
      {
        type: 'p',
        text:
          'Find it under **Operations › Upload Recordings** (Admins) or **Workspace › Upload Recordings** (QA Agents).',
      },
      {
        type: 'steps',
        items: [
          {
            title: 'Stay on the `File Upload` tab.',
            text: 'The other two tabs fetch from Genesys instead — see {{Fetch from Genesys|#genesys}}.',
          },
          {
            title: 'Pick the campaign, then the scorecard.',
            text:
              'Use `Choose Campaign`, then `Choose Scorecard`. The scorecard list shows only the scorecards attached to that campaign. Both are required — `Upload All` stays greyed out until they are set.',
          },
          {
            title: 'Drag your audio in, or use `Select Files`.',
            text:
              'Supported formats are WAV, MP3, M4A and FLAC, up to 100 MB each. Files that are not audio, or whose names are longer than 200 characters, are rejected with a warning.',
          },
          {
            title: 'Add metadata if you have it (optional).',
            text:
              'Click `Add Metadata` on a file to enter the `Agent`, `Call Date & Time` and `Call GUID / ID`. Values already in the file name (as `_AGENT_`, `_DT_` or `_GUID_` tags) are picked up and marked **in filename**.',
          },
          {
            title: 'Click `Upload All`.',
            text:
              'Each file shows its progress and ends as `Completed` or `Failed`. When the upload finishes you are taken back to your home page.',
          },
        ],
      },
      {
        type: 'ul',
        items: [
          '**Every upload becomes one job** on the Jobs page. Uploads of more than five files are sent through the batch queue.',
          '**Calls you upload are assigned to you.** A QA Agent finds them under **Assigned to me**.',
          '**Entering any metadata replaces the original file name** with one built from the campaign, scorecard and the metadata you gave.',
        ],
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'Where the campaign and scorecard get fixed',
        text:
          'Your choice is written into each call as it is uploaded. Calls already uploaded keep the campaign and scorecard they went in with, so check both before every upload.',
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'If a file name is too long',
        text:
          'The stored name adds the campaign, scorecard, your username and the job to the front of the file name. If that comes to more than 200 characters, the upload stops with **Filename Too Long**. Shorten the file name, or add metadata so the original name is not used.',
      },
    ],
  },

  {
    id: 'genesys',
    group: 'Bring calls in',
    title: 'Fetch from Genesys',
    lede: 'Pull recordings by date range and filters, or by conversation ID.',
    roles: ['sAdm', 'adm', 'qa'],
    blocks: [
      {
        type: 'p',
        text:
          'Both Genesys options are tabs on **Upload Recordings**: `Fetch from Genesys` and `Fetch from Genesys (Single)`. `Choose Campaign` and `Choose Scorecard` are required here too.',
      },
      { type: 'h3', text: 'Fetch from Genesys' },
      {
        type: 'steps',
        items: [
          {
            title: 'Set the `Start Date` and `End Date`.',
            text:
              'The range can be at most 7 days, the end date cannot be in the future, and the start must come before the end. Left blank, the range is the last 7 days up to now.',
          },
          {
            title: 'Add filters if you want to narrow the pull.',
            text:
              'All filters are optional: `Call Direction`, `Genesys Queues`, `Agents`, `Wrap-Up Codes`, `ANI (Caller Number)`, `DNIS (Dialed Number)` and `Division IDs`. Queues, agents and wrap-up codes are searched by name; type a division ID and press Enter. Each of these four takes several values.',
          },
          {
            title: 'Decide on per-agent sampling.',
            text: 'See below.',
          },
          {
            title: 'Click `Preview & Fetch Calls from Genesys`.',
            text:
              'The preview shows how many conversations match. Above 1,000 it shows a **Partial Count**, and the real number may be higher. If nothing matches, adjust the dates or filters.',
          },
          {
            title: 'Click `Confirm & Download`.',
            text:
              'The fetch runs in the background and the job’s page opens after a moment. Recordings are fetched in batches; the job page shows progress and lets an Admin stop or resume the fetch.',
          },
        ],
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'Two filter rules to remember',
        text:
          '**ANI and DNIS must be written as** `tel:+[number]` — for example `tel:+27115550100`. And two filters are always applied whether you set them or not: **voice calls only**, and **answered calls only**.',
      },
      { type: 'h3', text: 'Fetch from Genesys (Single)' },
      {
        type: 'p',
        text:
          'Fetch exact calls by `Conversation IDs`. Type an ID and press Enter (or a comma), or paste a list. Duplicates are dropped. Then click `Process Calls`. This is the fastest route when you are chasing a specific complaint.',
      },
      {
        type: 'p',
        text:
          'Conversations with no recording are **skipped** and listed in the message that appears, so fewer calls than IDs is expected rather than a failure. Calls fetched this way are assigned to you, like a file upload.',
      },
      { type: 'h3', text: 'Per-agent sampling' },
      {
        type: 'p',
        text:
          'Available on `Fetch from Genesys` and on scheduled jobs. Sampling QAs a set number of calls per agent instead of every call the filters return. Enter `Calls per agent` and pick a `Sample scope`: `Across whole date range` or `Per day`. The calls are spread evenly across time.',
      },
      {
        type: 'p',
        text:
          '**Leave it blank to QA every call.** Once the job runs, its page shows a **Per-Agent Sampling** summary of how many calls were kept per agent.',
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'After the fetch',
        text:
          'Calls from `Fetch from Genesys` are not assigned to you. An Admin shares them out with `Assign Job` on the job’s page (see {{Tracking jobs|#jobs}}) or from {{Assigning calls for review|#assigning}}. QA Agents cannot open the Jobs pages, so after a fetch the page may stay blank. The fetch has still started.',
      },
    ],
  },

  {
    id: 'cloud-connector',
    group: 'Bring calls in',
    title: 'Cloud Connector',
    lede: 'Copy recordings in from an S3 bucket outside this solution.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text:
          'Find it under **Operations › Cloud Connector**, or with the `Cloud Connector` button on Upload Recordings.',
      },
      {
        type: 'steps',
        items: [
          {
            title: 'Choose `Amazon S3` and enter the bucket.',
            text:
              'Enter the `Bucket Name *` (you can search the list or type one in), its `Region`, and optionally a `Prefix (Optional)` to copy only files under that path.',
          },
          {
            title: 'Click `Test Connection`.',
            text: 'This is required: `Start Copy` stays greyed out until the test succeeds.',
          },
          {
            title: 'Click `Load Bucket Contents` and select files.',
            text: 'Tick the files you want, or use the header checkbox to select every file shown.',
          },
          {
            title: 'Click `Start Copy`.',
            text: 'The files are copied in the background and you are returned to the home page.',
          },
        ],
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'FTP/SFTP is not available yet',
        text:
          'The `FTP/SFTP` option appears on the page, but its connection test always fails with "FTP connections are not supported yet." Use Amazon S3.',
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'What a copy does not do',
        text:
          'There is no campaign or scorecard choice here. Files are copied with their names unchanged and queued for processing, and a copy does not create its own job on the Jobs page.',
      },
    ],
  },

  {
    id: 'scheduled-jobs',
    group: 'Bring calls in',
    title: 'Scheduled jobs',
    lede: 'A recurring Genesys fetch instead of a manual one every morning.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text:
          'Find them under **Operations › Scheduled Jobs**. Existing schedules are listed under **Active Schedules**; build a new one under **Create New Schedule**. Each run appears on the Jobs page as its own job, with **SYSTEM_SCHEDULER** under Uploaded By.',
      },
      {
        type: 'steps',
        items: [
          {
            title: 'Basic Information.',
            text:
              'Enter a `Schedule Name` (at most **10 characters**), then pick the `Campaign` and `Scorecard`.',
          },
          {
            title: 'Run Frequency (SAST).',
            text:
              'Set `Repeat On` to `Every Day` or one weekday, then the `Hour (SAST)` and `Minute` (in 5-minute steps). Enter South African time; the tool converts it for you.',
          },
          {
            title: 'Data Extraction Window.',
            text:
              '`Scan From` and `Scan Until` are each a number of days ago, and From must be the same as Until or further back. The page then compares the window with the frequency and shows one of three messages:',
            items: [
              '**Perfect Match** — the window and frequency line up.',
              '**Overlap Warning** — the window is longer than the gap between runs. You are asked to confirm before the schedule saves.',
              '**Data Sampling Mode** — the window is shorter than the gap between runs. This is a note, not a warning.',
            ],
          },
          {
            title: 'Per-Agent Sampling (Optional).',
            text: 'Works as on Upload Recordings, with `Across whole window` or `Per day`.',
          },
          {
            title: 'Fetch Filters (Optional), then save.',
            text:
              'The same filters as a Genesys fetch. Click `Create Scheduled Job`.',
          },
        ],
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'How runs line up in practice',
        text:
          'The window applies in full only to a schedule’s **first** run. After that, each run starts where the previous run ended and runs to the end of the Scan Until day. So once a schedule has run, calls are not normally fetched twice and days between runs are not skipped, whatever the message said.',
      },
      {
        type: 'p',
        text:
          'Use the pencil (**Edit Filters & Schedule**) to change a schedule — `Update Scheduled Job` saves it, `Cancel Editing` drops your changes — or the bin (**Delete Schedule**) to remove it. To stop or resume a run that is already in progress, open that run’s job page.',
      },
    ],
  },

  {
    id: 'jobs',
    group: 'Bring calls in',
    title: 'Tracking jobs',
    lede: 'Where every upload and fetch is watched, assigned and exported.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text:
          'Find it under **Operations › Jobs**. A dot on the Jobs menu item means jobs have finished processing since you last opened the page.',
      },
      { type: 'h3', text: 'Upload Jobs' },
      {
        type: 'p',
        text:
          'Every upload, Genesys fetch and scheduled run is listed under **Recent Jobs**, with its campaign, template and who started it. Two columns measure different things and are easy to confuse:',
      },
      {
        type: 'ul',
        items: [
          '**Status** is processing — `Processing`, `Complete` or `Completed with failures`.',
          '**Assessed** is review progress — calls assessed out of calls in the job.',
        ],
      },
      {
        type: 'p',
        text:
          'The cards above the table count **jobs**: `Total Jobs`, `Assessed` (fully reviewed), `Awaiting assessment`, and `Failed` (finished with failures). Use `Load More Jobs` for older jobs, and click a row to open it.',
      },
      { type: 'h3', text: 'Job Details' },
      { type: 'p', text: 'A job’s page shows who started it, its call counts and every call in it. From here you can:' },
      {
        type: 'ul',
        items: [
          '**Track each file** under **Files In Progress**, as `Queued`, `Transcribing`, `Processing`, `Failed` or `Quota Stopped`.',
          '**Stop or resume a Genesys fetch** with `Stop Ingestion` / `Resume Ingestion`.',
          '**Read the per-agent sampling summary** — use `Show agents` for how many calls were sampled and available per agent.',
          '**Assign the job** with `Assign Job` — tick one or more QA Agents and click **Assign**. The job’s processed calls are shared between them in turn.',
          '**Export the calls** with `Export CSV` or `Export JSON`.',
        ],
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'Assign after processing',
        text:
          '`Assign Job` shares out only calls that have finished processing; with none yet it says **No Calls to Assign Yet**. Calls that finish later are not included, so assign again once the job is complete. Exports likewise cover only the calls loaded on the page. Use `Load More Calls` first, or you are asked to confirm an incomplete export.',
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'Quota Stopped',
        text:
          'When the daily transcription limit is reached, remaining files stop as `Quota Stopped` and the job shows a banner. A Super Admin raises the limit and resumes them on {{Transcribe Quota|#quota}}.',
      },
    ],
  },
];

export default ingestion;
